import { useMutation, useQueryClient } from "react-query";
import { type PostJsonError } from "../http/postJson";
import { useAgentsHttp } from "../useAgentsHttp";

// Each row goes through POST /projects/put. The backend dedupes by
// project_id (upsert), so re-running bulk import on the same file
// is idempotent. We loop in sequence rather than in parallel - the
// 17-projects scale doesn't need concurrency and sequential calls
// give clean per-row error attribution + a stable progress counter.

export interface BulkImportResult {
  ok_ids: string[];
  errors: { project_id: string; error: string }[];
}

export interface BulkPutInput {
  projects: Array<{ project_id: string; [key: string]: unknown }>;
  onProgress?: (done: number, total: number) => void;
}

export const useBulkPutProjects = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<BulkImportResult, Error, BulkPutInput>(
    async (input) => {
      const ok_ids: string[] = [];
      const errors: BulkImportResult["errors"] = [];
      for (let i = 0; i < input.projects.length; i++) {
        const p = input.projects[i];
        try {
          await http("/projects/put", p);
          ok_ids.push(p.project_id);
        } catch (e) {
          const err = e as PostJsonError;
          errors.push({
            project_id: p.project_id,
            error: err.message || String(e),
          });
        }
        input.onProgress?.(i + 1, input.projects.length);
      }
      return { ok_ids, errors };
    },
    {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ["inrange-agents-projects-list"] });
      },
    },
  );
};
